import { createContext, useCallback, useContext, useEffect, useState } from "react";
import merchantApi, { merchantTokenStore } from "../lib/merchantApi";

const Ctx = createContext({
  user: null,
  loading: true,
  signup: async () => {},
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function MerchantAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await merchantApi.get("/merchant/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only check session if a token exists — avoids the noisy 401 on first load.
    if (merchantTokenStore.get()) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [refresh]);

  const signup = useCallback(async (email, password, onboardingToken) => {
    const body = { email, password };
    if (onboardingToken) body.onboarding_token = onboardingToken;
    const { data } = await merchantApi.post("/merchant/signup", body);
    if (data?.token) merchantTokenStore.set(data.token);
    setUser(data.user);
    // Brief 2 sub-task 9.6 §2 — when the email is already in use, the
    // server returns ok+null so the response shape doesn't reveal which
    // case it was. Surface the data.user back so the caller can branch.
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await merchantApi.post("/merchant/login", { email, password });
    if (data?.token) merchantTokenStore.set(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await merchantApi.post("/merchant/logout"); }
    catch { /* ignore */ }
    merchantTokenStore.clear();
    setUser(null);
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, signup, login, logout, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useMerchantAuth() { return useContext(Ctx); }
